import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import axiosInstance from '../api';
import './MyTickets.css';

/**
 * Customer view of their own support tickets: list → conversation → reply. Strictly raiser-only
 * (the backend scopes every lookup to the logged-in user). Replying to a RESOLVED ticket reopens
 * it; CLOSED tickets are read-only.
 */
function MyTickets() {
  const { t } = useTranslation();
  const [tickets, setTickets] = useState([]);
  const [selected, setSelected] = useState(null);
  const [reply, setReply] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);

  const loadList = async () => {
    setLoading(true);
    try {
      const { data } = await axiosInstance.get('/support/tickets');
      setTickets(data || []);
    } catch {
      setError(t('MyTickets.loadError', 'Could not load your tickets.'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadList(); }, []);   // eslint-disable-line react-hooks/exhaustive-deps

  const openTicket = async (id) => {
    setError('');
    try {
      const { data } = await axiosInstance.get(`/support/tickets/${id}`);
      setSelected(data);
      setReply('');
    } catch {
      setError(t('MyTickets.loadError', 'Could not load your tickets.'));
    }
  };

  const sendReply = async () => {
    if (!reply.trim()) return;
    setSending(true);
    setError('');
    try {
      await axiosInstance.post(`/support/tickets/${selected.id}/replies`, { body: reply.trim() });
      setReply('');
      await openTicket(selected.id);
    } catch (err) {
      setError(err.response?.status === 409
        ? t('MyTickets.closed', 'This ticket is closed. Please raise a new ticket.')
        : t('MyTickets.sendError', 'Could not send your reply.'));
    } finally {
      setSending(false);
    }
  };

  const fmt = (s) => (s && s !== 'null' ? s.replace('T', ' ').slice(0, 16) : '—');

  if (selected) {
    const readOnly = selected.status === 'CLOSED';
    return (
      <div className="myt-page">
        <button className="myt-back" onClick={() => { setSelected(null); loadList(); }}>
          <i className="fas fa-arrow-left" /> {t('MyTickets.back', 'My tickets')}
        </button>
        {error && <p className="myt-error">{error}</p>}

        <div className="myt-head">
          <h2>{selected.ticketNumber}</h2>
          <span className={`myt-badge myt-${selected.status.toLowerCase()}`}>{selected.status}</span>
        </div>

        <div className="myt-msg myt-mine">
          <div className="myt-msg-head">
            <strong>{t('MyTickets.you', 'You')}</strong>
            <span>{fmt(selected.createdAt)}</span>
          </div>
          <p>{selected.message}</p>
        </div>

        {(selected.messages || []).map((m) => (
          m.authorType === 'SYSTEM' ? (
            <div key={m.id} className="myt-system">— {m.body} · {fmt(m.createdAt)} —</div>
          ) : (
            <div key={m.id} className={`myt-msg ${m.authorType === 'CUSTOMER' ? 'myt-mine' : 'myt-support'}`}>
              <div className="myt-msg-head">
                <strong>{m.authorType === 'CUSTOMER' ? t('MyTickets.you', 'You') : m.authorName}</strong>
                <span>{fmt(m.createdAt)}</span>
              </div>
              <p>{m.body}</p>
            </div>
          )
        ))}

        {readOnly ? (
          <p className="myt-closed-note">{t('MyTickets.closed', 'This ticket is closed. Please raise a new ticket.')}</p>
        ) : (
          <div className="myt-composer">
            <textarea rows={3} value={reply} onChange={(e) => setReply(e.target.value)}
              placeholder={t('MyTickets.replyPh', 'Write a reply…')} />
            <button className="myt-btn-primary" disabled={sending || !reply.trim()} onClick={sendReply}>
              {sending ? t('MyTickets.sending', 'Sending…') : t('MyTickets.reply', 'Reply')}
            </button>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="myt-page">
      {error && <p className="myt-error">{error}</p>}
      {loading ? (
        <p className="myt-empty">{t('MyTickets.loading', 'Loading…')}</p>
      ) : tickets.length === 0 ? (
        <p className="myt-empty">
          {t('MyTickets.empty', 'You have no support tickets yet. Use the "Need help?" button in the top bar to raise one.')}
        </p>
      ) : (
        <div className="myt-list">
          {tickets.map((tk) => (
            <button key={tk.id} className="myt-card" onClick={() => openTicket(tk.id)}>
              <div className="myt-card-top">
                <span className="myt-ticketno">{tk.ticketNumber}</span>
                <span className={`myt-badge myt-${tk.status.toLowerCase()}`}>{tk.status}</span>
              </div>
              <p className="myt-card-excerpt">{tk.excerpt}</p>
              <div className="myt-card-time">{t('MyTickets.lastActivity', 'Last activity')}: {fmt(tk.lastActivityAt)}</div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export default MyTickets;
